import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Paper,
  TextInput,
  Group,
  Button,
  Badge,
  Menu,
  ActionIcon,
  Stack,
  Select,
  Tabs,
  SimpleGrid,
  Card,
  Progress,
  Pagination,
  Modal,
  Skeleton,
  ThemeIcon,
  Box,
  Divider,
  Loader,
  Radio,
  Checkbox,
  Table,
  SegmentedControl,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconDots,
  IconEdit,
  IconTrash,
  IconPlus,
  IconChecklist,
  IconClock,
  IconRefresh,
  IconRocket,
  IconFileText,
  IconBrandSpotify,
  IconBrandX,
  IconBrandYoutube,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandWhatsapp,
  IconSend,
  IconLink,
  IconShare,
  IconDownload,
  IconCopy,
  IconX,
  IconLayoutGrid,
  IconList,
} from "@tabler/icons-react";
import axios from "axios";
import { ExportModal } from "../../../components/ExportModal";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";

type TaskType =
  | "spotify"
  | "youtube"
  | "twitter"
  | "facebook"
  | "instagram"
  | "whatsapp"
  | "telegram"
  | "tiktok"
  | "share"
  | "form"
  | "visit"
  | "other";

type TaskStatus = "active" | "completed" | "draft";

type Task = {
  id: string;
  title: string;
  description?: string;
  type: TaskType;
  points: number;
  status: TaskStatus;
  completions: number;
  targetCompletions: number;
  createdAt?: string;
  startDate?: string;
  expiresAt?: string;
  thumbnailUrl?: string;
  currency?: string;
  isTutorial?: boolean;
  squadsOnly?: boolean;
  sampleImages?: string[];
};

type SortColumn =
  | "title"
  | "type"
  | "points"
  | "status"
  | "createdAt"
  | "expiresAt"
  | "completions"
  | "targetCompletions";
type SortDirection = "asc" | "desc";

const MissionsPage = () => {
  useEffect(() => {
    document.title = "Missions | Virel Admin";
  }, []);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchInput, 400);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | null>(null);
  const [typeFilter, setTypeFilter] = useState<TaskType | null>(null);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15); // Make it 15 per page as requested
  const [sortState] = useState<{
    column: SortColumn;
    direction: SortDirection;
  }>({ column: "createdAt", direction: "desc" });

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [statusChangeModal, setStatusChangeModal] = useState<{
    opened: boolean;
    taskId: string | null;
    selectedStatus: TaskStatus | null;
    taskTitle: string | null;
  }>({ opened: false, taskId: null, selectedStatus: null, taskTitle: null });
  const [statusChangeConfirmed, setStatusChangeConfirmed] = useState(false);
  const [duplicateConfirmModal, setDuplicateConfirmModal] = useState<{
    opened: boolean;
    taskId: string | null;
    taskTitle: string | null;
  }>({ opened: false, taskId: null, taskTitle: null });
  const [duplicateStatus, setDuplicateStatus] = useState<TaskStatus>("draft");
  const [duplicateTitle, setDuplicateTitle] = useState<string>("");
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Bulk Selection State
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkUpdatingStatus, setIsBulkUpdatingStatus] = useState(false);
  const [bulkStatusModal, setBulkStatusModal] = useState(false);
  const [bulkSelectedStatus, setBulkSelectedStatus] = useState<TaskStatus>("active");
  const [bulkStatusConfirmed, setBulkStatusConfirmed] = useState(false);

  const derivedStatus =
    statusFilter ||
    (activeTab !== "all" && activeTab !== "tutorial"
      ? (activeTab as TaskStatus)
      : undefined);

  const tasksEndpoint = useMemo(
    () =>
      Endpoints.GetMissions({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        status: derivedStatus || undefined,
        type: typeFilter || undefined,
        sortBy: sortState.column,
        sortDir: sortState.direction,
        isTutorial:
          activeTab === "tutorial"
            ? true
            : derivedStatus === "active"
              ? false
              : undefined,
      }),
    [
      currentPage,
      pageSize,
      debouncedSearch,
      derivedStatus,
      typeFilter,
      sortState,
      activeTab,
    ],
  );

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<Task[]>
  >({
    method: tasksEndpoint.method,
    route: tasksEndpoint.route,
  });

  const tasks = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));

  // Note: These are rough stats from loaded data in some cases, or need separate endpoints.
  // We'll keep them simple or remove if inaccurate.
  // For now, let's just show filtered counts if possible, or remove the stats block to avoid confusion if we don't have global stats.
  // Let's remove the Stats Grid for now as it requires global counts which GetMissions (filtered) doesn't return (it returns filtered count).

  const handleStatusChange = async () => {
    if (!statusChangeModal.taskId || !statusChangeModal.selectedStatus) return;

    // Using UpdateMission endpoint
    const endpoint = Endpoints.UpdateMission(statusChangeModal.taskId);
    await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
      data: { status: statusChangeModal.selectedStatus },
    });

    setStatusChangeModal({ ...statusChangeModal, opened: false });
    setStatusChangeConfirmed(false);
    reloadData();
  };

  const openDuplicateModal = (task: Task) => {
    setDuplicateStatus("draft");
    setDuplicateTitle(`${task.title} (Copy)`);
    setDuplicateConfirmModal({
      opened: true,
      taskId: task.id,
      taskTitle: task.title,
    });
  };

  const handleDuplicate = async () => {
    if (!duplicateConfirmModal.taskId) return;
    try {
      setDuplicatingId(duplicateConfirmModal.taskId);
      setDuplicateConfirmModal({ ...duplicateConfirmModal, opened: false }); // Close modal immediately or wait? prompt says confirm modal BEFORE duplicating.

      const endpoint = Endpoints.DuplicateMission(duplicateConfirmModal.taskId);
      await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          targetStatus: duplicateStatus,
          title: duplicateTitle.trim() || undefined,
        },
      });

      await reloadData();
    } catch (e) {
      console.error("Duplicate failed", e);
    } finally {
      setDuplicatingId(null);
      // Reset modal state if needed, already closed
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const endpoint = Endpoints.DeleteMission(deleteId);
    await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
    });
    setDeleteId(null);
    reloadData();
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setIsBulkDeleting(true);

    try {
      // Execute all deletes in parallel
      await Promise.all(
        selectedIds.map((id) =>
          PerformRequest({
            method: Endpoints.DeleteMission(id).method,
            route: Endpoints.DeleteMission(id).route,
          }),
        ),
      );

      setSelectedIds([]);
      setIsSelectionMode(false);
      setBulkDeleteModalOpen(false);
      reloadData();
    } catch (error) {
      console.error("Bulk delete failed", error);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkStatusUpdate = async (status: TaskStatus) => {
    if (selectedIds.length === 0) return;
    setIsBulkUpdatingStatus(true);

    try {
      const endpoint = Endpoints.BulkUpdateMissionStatus();
      await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          ids: selectedIds,
          status,
        },
      });

      setSelectedIds([]);
      setIsSelectionMode(false);
      reloadData();
    } catch (error) {
      console.error("Bulk status update failed", error);
    } finally {
      setIsBulkUpdatingStatus(false);
    }
  };

  const getTypeIcon = (type: TaskType) => {
    switch (type) {
      case "spotify":
        return <IconBrandSpotify size={20} />;
      case "youtube":
        return <IconBrandYoutube size={20} />;
      case "twitter":
        return <IconBrandX size={20} />;
      case "facebook":
        return <IconBrandFacebook size={20} />;
      case "instagram":
        return <IconBrandInstagram size={20} />;
      case "tiktok":
        return <IconBrandTiktok size={20} />;
      case "whatsapp":
        return <IconBrandWhatsapp size={20} />;
      case "telegram":
        return <IconSend size={20} />;
      case "share":
        return <IconShare size={20} />;
      case "visit":
        return <IconLink size={20} />;
      case "form":
        return <IconChecklist size={20} />;
      default:
        return <IconRocket size={20} />;
    }
  };

  const getTypeColor = (type: TaskType) => {
    switch (type) {
      case "spotify":
        return "green";
      case "youtube":
        return "red";
      case "twitter":
        return "dark";
      case "facebook":
        return "blue";
      case "instagram":
        return "pink";
      case "whatsapp":
        return "green";
      case "telegram":
        return "cyan";
      case "tiktok":
        return "dark";
      case "visit":
        return "green";
      default:
        return "gray";
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case "active":
        return "teal";
      case "completed":
        return "green";
      case "draft":
        return "yellow";
      default:
        return "gray";
    }
  };

  const MissionCard = ({ task }: { task: Task }) => {
    const safeCompletions = task.completions || 0;
    const safeTargetCompletions = task.targetCompletions || 0;

    const hasImages = task.sampleImages && task.sampleImages.length > 0;

    const percentage =
      safeTargetCompletions > 0
        ? Math.min((safeCompletions / safeTargetCompletions) * 100, 100)
        : 0;
    // Tutorial opportunities never expire
    const isExpired =
      !task.isTutorial &&
      !!task.expiresAt &&
      new Date(task.expiresAt) < new Date();

    return (
      <Card
        padding="lg"
        radius="md"
        withBorder
        className="mission-card"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderColor: !hasImages ? "var(--mantine-color-red-6)" : undefined,
          borderWidth: !hasImages ? "2px" : undefined,
        }}
      >
        <Group justify="space-between" mb="xs">
          <Badge
            color={!hasImages ? "red" : getTypeColor(task.type)}
            variant="light"
            leftSection={getTypeIcon(task.type)}
            radius="sm"
          >
            {task.type.toUpperCase()}
          </Badge>
          {isSelectionMode ? (
            <Checkbox
              checked={selectedIds.includes(task.id)}
              onChange={() => toggleSelection(task.id)}
              size="md"
              color="black"
              aria-label="Select mission"
            />
          ) : (
            <Menu position="bottom-end" shadow="sm">
              <Menu.Target>
                <ActionIcon variant="transparent" color="gray">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  component={NavLink}
                  to={`/d/missions/${task.id}`}
                  leftSection={<IconFileText size={14} />}
                >
                  View Details
                </Menu.Item>
                <Menu.Item
                  component={NavLink}
                  to={`/d/missions/update/${task.id}`}
                  leftSection={<IconEdit size={14} />}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    duplicatingId === task.id ? (
                      <Loader size={12} color="dimmed" />
                    ) : (
                      <IconCopy size={14} />
                    )
                  }
                  onClick={() => openDuplicateModal(task)}
                  disabled={!!duplicatingId}
                >
                  {duplicatingId === task.id ? "Duplicating..." : "Duplicate"}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => {
                    setStatusChangeConfirmed(false);
                    setStatusChangeModal({
                      opened: true,
                      taskId: task.id,
                      selectedStatus: task.status === "active" ? "draft" : "active",
                      taskTitle: task.title,
                    });
                  }}
                >
                  Update Status
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => setDeleteId(task.id)}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>

        <Group gap={6} mt="xs">
          {task.isTutorial && (
            <Badge color="grape" variant="dot" size="xs">
              Tutorial
            </Badge>
          )}
          {task.squadsOnly && (
            <Badge color="violet" variant="dot" size="xs">
              Squads Only
            </Badge>
          )}
          <Text
            fw={700}
            size="lg"
            lineClamp={1}
            title={task.title}
            c={!hasImages ? "red.6" : undefined}
          >
            {task.title}
          </Text>
        </Group>
        <Text size="sm" c="dimmed" lineClamp={2} mt={4} h={42}>
          {task.description || "No description provided."}
        </Text>

        {/* --- START/END DATE DISPLAY --- */}
        <Group mt="xs" mb={4} gap="xs">
          {task.startDate ? (
            <Badge size="sm" color="blue" variant="light">
              Starts:{" "}
              {new Date(task.startDate).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </Badge>
          ) : (
            <Badge size="sm" color="gray" variant="light">
              Starts: Immediately
            </Badge>
          )}

          {task.expiresAt ? (
            <Badge size="sm" color="orange" variant="light">
              Ends:{" "}
              {new Date(task.expiresAt).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })}
            </Badge>
          ) : (
            <Badge size="sm" color="gray" variant="light">
              Ends: Never
            </Badge>
          )}
        </Group>

        <Group mt="md" mb="md">
          <Box style={{ flex: 1 }}>
            <Group justify="space-between" mb={4}>
              <Text size="xs" fw={700} c="dimmed">
                PROGRESS
              </Text>
              <Text size="xs" fw={700}>
                {Math.round(percentage)}%
              </Text>
            </Group>
            <Progress
              value={percentage}
              color={percentage >= 100 ? "green" : "green"}
              size="sm"
              radius="xl"
            />
            <Text size="xs" c="dimmed" mt={4}>
              {safeCompletions.toLocaleString()} /{" "}
              {safeTargetCompletions.toLocaleString()} completions
            </Text>
          </Box>
        </Group>

        <Divider mb="md" />

        <Group justify="space-between" mt="auto">
          <div>
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              Reward
            </Text>
            <Group gap={4} align="center">
              <Text fw={800} size="lg" c="green.7">
                {task.points}
              </Text>
              <Text size="xs" fw={700} c="dimmed">
                Coins
              </Text>
            </Group>
          </div>

          <Badge size="lg" variant="dot" color={getStatusColor(task.status)}>
            {task.status}
          </Badge>
        </Group>

        {isExpired && (
          <Box
            pos="absolute"
            bottom={0}
            left={0}
            right={0}
            bg="orange.1"
            py={2}
            px="md"
            style={{ borderTop: "1px solid var(--mantine-color-orange-2)" }}
          >
            <Group gap={6} justify="center">
              <IconClock size={12} color="var(--mantine-color-orange-6)" />
              <Text size="xs" c="orange.8" fw={600}>
                Expired
              </Text>
            </Group>
          </Box>
        )}
      </Card>
    );
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="end">
          <div>
            <Title
              order={1}
              fw={800}
              fz={32}
              style={{ letterSpacing: "-0.5px" }}
            >
              Activities
            </Title>
            <Text c="dimmed" size="lg" mt={4}>
              Manage user activities and rewards
            </Text>
          </div>
          <Group>
            {isSelectionMode ? (
              <>
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={() => {
                    setIsSelectionMode(false);
                    setSelectedIds([]);
                  }}
                  leftSection={<IconX size={16} />}
                >
                  Cancel
                </Button>
                {selectedIds.length > 0 && (
                  <Group gap="xs">
                    <Button
                      variant="light"
                      color="blue"
                      leftSection={<IconRefresh size={16} />}
                      onClick={() => {
                        setBulkSelectedStatus("active");
                        setBulkStatusConfirmed(false);
                        setBulkStatusModal(true);
                      }}
                      loading={isBulkUpdatingStatus}
                    >
                      Update Status ({selectedIds.length})
                    </Button>
                    <Button
                      color="red"
                      leftSection={<IconTrash size={16} />}
                      onClick={() => setBulkDeleteModalOpen(true)}
                      loading={isBulkDeleting}
                    >
                      Delete ({selectedIds.length})
                    </Button>
                  </Group>
                )}
              </>
            ) : (
              <Button
                variant="default"
                onClick={() => setIsSelectionMode(true)}
              >
                Select
              </Button>
            )}
            <SegmentedControl
              value={viewMode}
              onChange={(v) => setViewMode(v as "grid" | "table")}
              data={[
                { label: <IconLayoutGrid size={18} />, value: "grid" },
                { label: <IconList size={18} />, value: "table" },
              ]}
              size="sm"
              title={viewMode === "grid" ? "Card view" : "Table view"}
            />
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="default"
              onClick={() => reloadData()}
            >
              Refresh
            </Button>
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              onClick={() => setExportModalOpen(true)}
            >
              Export
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              size="md"
              color="black"
              component={NavLink}
              to="/d/missions/new"
            >
              Create Activity
            </Button>
          </Group>
        </Group>

        <Card withBorder padding="sm" radius="md" bg="var(--bg-surface-2)">
          <Group gap="sm">
            <TextInput
              placeholder="Search activities..."
              leftSection={<IconSearch size={16} />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Type"
              data={[
                { value: "spotify", label: "Spotify" },
                { value: "youtube", label: "YouTube" },
                { value: "twitter", label: "Twitter / X" },
                { value: "facebook", label: "Facebook" },
                { value: "instagram", label: "Instagram" },
                { value: "whatsapp", label: "WhatsApp" },
                { value: "telegram", label: "Telegram" },
                { value: "tiktok", label: "TikTok" },
                { value: "visit", label: "Visit Link" },
                { value: "share", label: "Share" },
              ]}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v as TaskType)}
              clearable
              w={160}
            />
            <Select
              placeholder="Status"
              data={[
                { value: "active", label: "Active" },
                { value: "draft", label: "Draft" },
                { value: "completed", label: "Completed" },
              ]}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as TaskStatus)}
              clearable
              w={140}
            />
          </Group>
        </Card>

        <Tabs
          value={activeTab}
          onChange={(v) => {
            setActiveTab(v || "all");
            setCurrentPage(1);
          }}
          variant="pills"
          radius="md"
        >
          <Tabs.List>
            <Tabs.Tab value="all">All Activities</Tabs.Tab>
            <Tabs.Tab value="active">Active</Tabs.Tab>
            <Tabs.Tab value="draft">Drafts</Tabs.Tab>
            <Tabs.Tab value="completed">Completed</Tabs.Tab>
            <Tabs.Tab value="tutorial">Tutorials</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {isLoading ? (
          viewMode === "table" ? (
            <Paper withBorder radius="md" p="md">
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Title</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Progress</Table.Th>
                    <Table.Th>Reward</Table.Th>
                    <Table.Th>Date Created</Table.Th>
                    <Table.Th></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>
                        <Skeleton height={20} width="80%" />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={60} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={60} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={80} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={40} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={80} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={28} width={28} circle />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="lg">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} height={250} radius="md" />
              ))}
            </SimpleGrid>
          )
        ) : tasks.length === 0 ? (
          <Paper p="xl" withBorder radius="md" ta="center" bg="var(--bg-surface-2)">
            <ThemeIcon
              size={60}
              radius="xl"
              color="gray"
              variant="light"
              mb="md"
            >
              <IconRocket size={30} />
            </ThemeIcon>
            <Title order={3} mb="xs">
              No Activities Found
            </Title>
            <Text c="dimmed" mb="lg">
              Get started by creating your first activity.
            </Text>
            <Button component={NavLink} to="/d/missions/new">
              Create Activity
            </Button>
          </Paper>
        ) : viewMode === "table" ? (
          <Stack gap="md">
            <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    {isSelectionMode && (
                      <Table.Th style={{ width: 40 }}>Select</Table.Th>
                    )}
                    <Table.Th>Title</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Progress</Table.Th>
                    <Table.Th>Reward</Table.Th>
                    <Table.Th>Date Created</Table.Th>
                    <Table.Th>Dates</Table.Th>
                    <Table.Th>Expired</Table.Th>
                    <Table.Th style={{ width: 50 }}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tasks.map((task) => {
                    const safeCompletions = task.completions || 0;
                    const safeTargetCompletions = task.targetCompletions || 0;
                    const percentage =
                      safeTargetCompletions > 0
                        ? Math.min(
                            (safeCompletions / safeTargetCompletions) * 100,
                            100,
                          )
                        : 0;
                    const isExpired =
                      !task.isTutorial &&
                      !!task.expiresAt &&
                      new Date(task.expiresAt) < new Date();
                    return (
                      <Table.Tr key={task.id}>
                        {isSelectionMode && (
                          <Table.Td>
                            <Checkbox
                              checked={selectedIds.includes(task.id)}
                              onChange={() => toggleSelection(task.id)}
                              size="sm"
                              color="black"
                              aria-label="Select"
                            />
                          </Table.Td>
                        )}
                        <Table.Td>
                          <Group gap={6}>
                            {task.isTutorial && (
                              <Badge color="grape" variant="dot" size="xs">
                                Tutorial
                              </Badge>
                            )}
                            {task.squadsOnly && (
                              <Badge color="violet" variant="dot" size="xs">
                                Squads Only
                              </Badge>
                            )}
                            <Text
                              fw={500}
                              size="sm"
                              component={NavLink}
                              to={`/d/missions/${task.id}`}
                            >
                              {task.title}
                            </Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={getTypeColor(task.type)}
                            variant="light"
                            size="sm"
                            leftSection={getTypeIcon(task.type)}
                          >
                            {task.type}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            variant="dot"
                            color={getStatusColor(task.status)}
                          >
                            {task.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {safeCompletions.toLocaleString()} /{" "}
                            {safeTargetCompletions.toLocaleString()} (
                            {Math.round(percentage)}%)
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text fw={600} size="sm" c="green.7">
                            {task.points} Coins
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {task.createdAt
                              ? new Date(task.createdAt).toLocaleDateString(
                                  undefined,
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )
                              : "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {task.startDate
                              ? new Date(task.startDate).toLocaleDateString(
                                  undefined,
                                  { month: "short", day: "numeric" },
                                )
                              : "Immediate"}
                            {" → "}
                            {task.expiresAt
                              ? new Date(task.expiresAt).toLocaleDateString(
                                  undefined,
                                  { month: "short", day: "numeric" },
                                )
                              : "Never"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {isExpired ? (
                            <Badge size="sm" color="orange" variant="light">
                              Expired
                            </Badge>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {!isSelectionMode && (
                            <Menu position="bottom-end" shadow="sm">
                              <Menu.Target>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  size="sm"
                                >
                                  <IconDots size={16} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  component={NavLink}
                                  to={`/d/missions/${task.id}`}
                                  leftSection={<IconFileText size={14} />}
                                >
                                  View Details
                                </Menu.Item>
                                <Menu.Item
                                  component={NavLink}
                                  to={`/d/missions/update/${task.id}`}
                                  leftSection={<IconEdit size={14} />}
                                >
                                  Edit
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={
                                    duplicatingId === task.id ? (
                                      <Loader size={12} color="dimmed" />
                                    ) : (
                                      <IconCopy size={14} />
                                    )
                                  }
                                  onClick={() => openDuplicateModal(task)}
                                  disabled={!!duplicatingId}
                                >
                                  {duplicatingId === task.id
                                    ? "Duplicating..."
                                    : "Duplicate"}
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<IconRefresh size={14} />}
                                  onClick={() => {
                                    setStatusChangeConfirmed(false);
                                    setStatusChangeModal({
                                      opened: true,
                                      taskId: task.id,
                                      selectedStatus: task.status === "active" ? "draft" : "active",
                                      taskTitle: task.title,
                                    });
                                  }}
                                >
                                  Update Status
                                </Menu.Item>
                                <Menu.Divider />
                                <Menu.Item
                                  color="red"
                                  leftSection={<IconTrash size={14} />}
                                  onClick={() => setDeleteId(task.id)}
                                >
                                  Delete
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Paper>
            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  total={totalPages}
                  value={currentPage}
                  onChange={setCurrentPage}
                />
              </Group>
            )}
          </Stack>
        ) : (
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="lg">
              {tasks.map((task) => (
                <MissionCard key={task.id} task={task} />
              ))}
            </SimpleGrid>

            {totalPages > 1 && (
              <Group justify="center" mt="xl">
                <Pagination
                  total={totalPages}
                  value={currentPage}
                  onChange={setCurrentPage}
                />
              </Group>
            )}
          </Stack>
        )}
      </Stack>

      {/* Confirmation Modals */}
      <Modal
        opened={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete Activity"
        centered
      >
        <Text size="sm">
          Are you sure you want to delete this activity? This action cannot be
          undone.
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setDeleteId(null)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete}>
            Delete
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={bulkDeleteModalOpen}
        onClose={() => setBulkDeleteModalOpen(false)}
        title={
          <Group gap="sm">
            <IconTrash size={20} color="red" />
            <Text fw={700}>Delete Activities</Text>
          </Group>
        }
        centered
        padding="lg"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete{" "}
            <strong>{selectedIds.length}</strong> selected activities? This
            action cannot be undone.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={() => setBulkDeleteModalOpen(false)}
              disabled={isBulkDeleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleBulkDelete}
              loading={isBulkDeleting}
            >
              Delete {selectedIds.length} Activities
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={statusChangeModal.opened}
        onClose={() => {
          setStatusChangeModal({ ...statusChangeModal, opened: false });
          setStatusChangeConfirmed(false);
        }}
        title="Update Mission Status"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Changing status for{" "}
            <Text span fw={700} c="dark" inherit>
              {statusChangeModal.taskTitle}
            </Text>
          </Text>

          <Radio.Group
            label="Select new status"
            value={statusChangeModal.selectedStatus ?? ""}
            onChange={(val) =>
              setStatusChangeModal({
                ...statusChangeModal,
                selectedStatus: val as TaskStatus,
              })
            }
          >
            <Stack gap="xs" mt="xs">
              <Radio value="active" label="Active (Published)" color="teal" />
              <Radio value="draft" label="Draft" color="yellow" />
            </Stack>
          </Radio.Group>

          <Checkbox
            label="I confirm I want to change the status of this mission"
            checked={statusChangeConfirmed}
            onChange={(e) => setStatusChangeConfirmed(e.currentTarget.checked)}
            color="dark"
          />

          <Group justify="flex-end" mt="xs">
            <Button
              variant="default"
              onClick={() => {
                setStatusChangeModal({ ...statusChangeModal, opened: false });
                setStatusChangeConfirmed(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleStatusChange}
              disabled={!statusChangeConfirmed || !statusChangeModal.selectedStatus}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={bulkStatusModal}
        onClose={() => {
          setBulkStatusModal(false);
          setBulkStatusConfirmed(false);
        }}
        title="Update Status"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Update the status of{" "}
            <Text span fw={700} c="dark" inherit>
              {selectedIds.length} selected{" "}
              {selectedIds.length === 1 ? "mission" : "missions"}
            </Text>
          </Text>

          <Radio.Group
            label="Select new status"
            value={bulkSelectedStatus}
            onChange={(val) => setBulkSelectedStatus(val as TaskStatus)}
          >
            <Stack gap="xs" mt="xs">
              <Radio value="active" label="Active (Published)" color="teal" />
              <Radio value="draft" label="Draft" color="yellow" />
            </Stack>
          </Radio.Group>

          <Checkbox
            label={`I confirm I want to update ${selectedIds.length} ${selectedIds.length === 1 ? "mission" : "missions"} to ${bulkSelectedStatus}`}
            checked={bulkStatusConfirmed}
            onChange={(e) => setBulkStatusConfirmed(e.currentTarget.checked)}
            color="dark"
          />

          <Group justify="flex-end" mt="xs">
            <Button
              variant="default"
              onClick={() => {
                setBulkStatusModal(false);
                setBulkStatusConfirmed(false);
              }}
              disabled={isBulkUpdatingStatus}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={async () => {
                await handleBulkStatusUpdate(bulkSelectedStatus);
                setBulkStatusModal(false);
                setBulkStatusConfirmed(false);
              }}
              loading={isBulkUpdatingStatus}
              disabled={!bulkStatusConfirmed}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={duplicateConfirmModal.opened}
        onClose={() =>
          setDuplicateConfirmModal({ ...duplicateConfirmModal, opened: false })
        }
        title="Duplicate Activity"
        centered
      >
        <Text size="sm" mb="md">
          You are duplicating "
          <strong>{duplicateConfirmModal.taskTitle}</strong>".
        </Text>

        <TextInput
          label="New Activity Title"
          description="Leave as is for default or enter a custom title"
          placeholder="Enter title for duplicated activity"
          value={duplicateTitle}
          onChange={(e) => setDuplicateTitle(e.currentTarget.value)}
          mb="md"
        />

        <Radio.Group
          label="Set status for new activity"
          value={duplicateStatus}
          onChange={(val) => setDuplicateStatus(val as TaskStatus)}
          mb="md"
        >
          <Group mt="xs">
            <Radio value="draft" label="Draft" />
            <Radio value="active" label="Active (Published)" />
          </Group>
        </Radio.Group>
        <Group justify="flex-end" mt="md">
          <Button
            variant="default"
            onClick={() =>
              setDuplicateConfirmModal({
                ...duplicateConfirmModal,
                opened: false,
              })
            }
          >
            Cancel
          </Button>
          <Button color="green" onClick={handleDuplicate}>
            Duplicate
          </Button>
        </Group>
      </Modal>

      <ExportModal
        opened={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        title="Export Activities Report"
        type="activities"
        additionalFilters={null}
        onExport={async (dateFilters) => {
          const token = localStorage.getItem("t");
          const endpoint = Endpoints.GetActivitiesReport({
            format: "csv",
            ...dateFilters,
          });

          const response = await axios.get(endpoint.route, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          });

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `activities_report_${new Date().toISOString()}.csv`,
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
      />
    </Container>
  );
};

export default MissionsPage;
